import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { DependencyResponse } from '../../../../shared/dependenciesV2';

import { getBaseDependencyFetchQueryKey } from './queryKey';

export const getReleaseGroupDependencyQueryKey = (
  releaseGroupId: number,
  releaseGroupReleaseId: number,
  dependencyRevisionLocator: string,
): QueryKey => [
  ...getBaseDependencyFetchQueryKey(dependencyRevisionLocator),
  'release-groups',
  releaseGroupId,
  'releases',
  releaseGroupReleaseId,
];

/**
 * Hook to fetch a release group dependency.
 * @param releaseGroupId - The ID of the release group.
 * @param releaseGroupReleaseId - The ID of the release group release.
 * @param dependencyRevisionLocator - The locator of the dependency revision.
 * @param shouldFetch - Whether to fetch the dependency.
 * @returns The dependency.
 */
export default function useGetReleaseGroupDependency(
  releaseGroupId: number,
  releaseGroupReleaseId: number,
  dependencyRevisionLocator: string,
  shouldFetch: boolean = true,
) {
  const queryKey: QueryKey = getReleaseGroupDependencyQueryKey(
    releaseGroupId,
    releaseGroupReleaseId,
    dependencyRevisionLocator,
  );

  const queryFn = () =>
    get<DependencyResponse>(
      `/api/v2/release-groups/${releaseGroupId}/releases/${releaseGroupReleaseId}/dependencies/${nuc(dependencyRevisionLocator)}`,
    );

  return useQuery<DependencyResponse>({
    queryKey,
    queryFn,
    enabled: shouldFetch,
  });
}
